import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { AdminProvider, useAdmin } from './contexts/AdminContext';

// Layout (always needed — eager)
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import BottomNav from './components/layout/BottomNav';

// Lazy overlays — never visible on first paint, no need to block main bundle
const Toaster = lazy(() => import('./components/ui/sonner').then(m => ({ default: m.Toaster })));
const CartDrawer = lazy(() => import('./components/cart/CartDrawer'));
const AuthModal = lazy(() => import('./components/auth/AuthModal'));

// All pages lazy (framer-motion deferred out of main bundle)
const HomePage = lazy(() => import('./pages/HomePage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const ProductPage = lazy(() => import('./pages/ProductPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('./pages/CheckoutSuccessPage'));
const ResumePaymentPage = lazy(() => import('./pages/ResumePaymentPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

// Lazy-loaded public pages
const RoutinePage = lazy(() => import('./pages/RoutinePage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const DrainagePage = lazy(() => import('./pages/DrainagePage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const FormationsMassagesPage = lazy(() => import('./pages/FormationsMassagesPage'));
const IdeesCadeauxPage = lazy(() => import('./pages/IdeesCadeauxPage'));
const GiftCardPage = lazy(() => import('./pages/GiftCardPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const LivraisonPage = lazy(() => import('./pages/LivraisonPage'));
const RetoursPage = lazy(() => import('./pages/RetoursPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const ProPage = lazy(() => import('./pages/ProPage'));
const PublicInvoicePage = lazy(() => import('./pages/PublicInvoicePage'));

// Lazy-loaded admin pages
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminPages = lazy(() => import('./pages/admin/AdminPages'));
const AdminMedia = lazy(() => import('./pages/admin/AdminMedia'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminContent = lazy(() => import('./pages/admin/AdminContent'));
const AdminDiscounts = lazy(() => import('./pages/admin/AdminDiscounts'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const AdminGiftCards = lazy(() => import('./pages/admin/AdminGiftCards'));
const AdminFormationApplications = lazy(() => import('./pages/admin/AdminFormationApplications'));
const AdminEmailSettings = lazy(() => import('./pages/admin/AdminEmailSettings'));
const AdminEmailTemplates = lazy(() => import('./pages/admin/AdminEmailTemplates'));
const AdminEmailBounces = lazy(() => import('./pages/admin/AdminEmailBounces'));
const AdminCommunications = lazy(() => import('./pages/admin/AdminCommunications'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const AdminSEO = lazy(() => import('./pages/admin/AdminSEO'));
const AdminTeam = lazy(() => import('./pages/admin/AdminTeam'));
const AdminMarketing = lazy(() => import('./pages/admin/AdminMarketing'));
const AdminStock = lazy(() => import('./pages/admin/AdminStock'));
const AdminTutorial = lazy(() => import('./pages/admin/AdminTutorial'));
const AdminPro = lazy(() => import('./pages/admin/AdminPro'));
const AdminLeads = lazy(() => import('./pages/admin/AdminLeads'));
const AdminManualOrders = lazy(() => import('./pages/admin/AdminManualOrders'));
const AdminTasks = lazy(() => import('./pages/admin/AdminTasks'));
const AdminInternalLinks = lazy(() => import('./pages/admin/AdminInternalLinks'));
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions'));
const AdminAccounting = lazy(() => import('./pages/admin/AdminAccounting'));

// Subscription pages
const AbonnementPage = lazy(() => import('./pages/AbonnementPage'));
const AbonnementMerciPage = lazy(() => import('./pages/AbonnementMerciPage'));

const CookieBanner = lazy(() => import('./components/ui/CookieBanner'));
const TrackingManager = lazy(() => import('./components/tracking/TrackingManager'));
import { useAttribution } from './hooks/useAttribution';

// Pro
import { ProProvider } from './context/ProContext';

import './App.css';

const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-brand-forest/30 border-t-brand-forest rounded-full animate-spin" />
  </div>
);

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Capture UTM/GCLID params on every route
function AttributionCapture() {
  useAttribution();
  return null;
}

// Layout wrapper for pages that need header/footer
function MainLayout({ children }) {
  return (
    <>
      <Header />
      <main className="min-h-screen">
        {children}
      </main>
      <Footer />
      <BottomNav />
      <Suspense fallback={null}><CartDrawer /></Suspense>
      <Suspense fallback={null}><AuthModal /></Suspense>
    </>
  );
}

// Checkout has its own layout (no header/footer/bottom nav)
function CheckoutLayout({ children }) {
  return (
    <>
      {children}
      <Suspense fallback={null}><CartDrawer /></Suspense>
      <Suspense fallback={null}><AuthModal /></Suspense>
    </>
  );
}

// Admin guard
function AdminGuard({ children }) {
  const { admin, loading } = useAdmin();
  if (loading) return <div className="min-h-screen bg-[#0f1117] flex items-center justify-center"><div className="text-gray-500">Chargement...</div></div>;
  if (!admin) return <AdminLogin />;
  return children;
}

// ─── Global Error Boundary ────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[ErrorBoundary]', error, info); }
  render() {
    if (this.state.hasError) return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-brand-cream px-4 text-center">
        <p className="text-5xl mb-6">🌿</p>
        <h1 className="font-serif text-2xl text-brand-charcoal mb-3">Une erreur est survenue</h1>
        <p className="text-brand-stone mb-6 max-w-sm">Rechargez la page ou contactez-nous si le problème persiste.</p>
        <button onClick={() => window.location.reload()} className="bg-brand-forest text-white px-8 py-3 rounded-full font-semibold hover:bg-brand-forest/90 transition-colors">
          Recharger la page
        </button>
      </div>
    );
    return this.props.children;
  }
}

function App() {
  const [nonCriticalReady, setNonCriticalReady] = useState(false);

  useEffect(() => {
    const loadNonCriticalCss = () => {
      import('./styles/nonCriticalAnimations.css').catch(() => {});
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(loadNonCriticalCss, { timeout: 1500 });
      return;
    }

    const timer = window.setTimeout(loadNonCriticalCss, 1);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const enableNonCritical = () => setNonCriticalReady(true);

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(enableNonCritical, { timeout: 2000 });
      return;
    }

    const timer = window.setTimeout(enableNonCritical, 1200);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <ErrorBoundary>
    <BrowserRouter>
      <ScrollToTop />
      <AttributionCapture />
      <AuthProvider>
        <CartProvider>
          <ProProvider>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Admin */}
              <Route path="/admin/*" element={
                <AdminProvider>
                  <AdminGuard>
                    <Routes>
                      <Route element={<AdminLayout />}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="content" element={<AdminContent />} />
                        <Route path="products" element={<AdminProducts />} />
                        <Route path="categories" element={<AdminCategories />} />
                        <Route path="discounts" element={<AdminDiscounts />} />
                        <Route path="gift-cards" element={<AdminGiftCards />} />
                        <Route path="formation-applications" element={<AdminFormationApplications />} />
                        <Route path="orders" element={<AdminOrders />} />
                        <Route path="pages" element={<AdminPages />} />
                        <Route path="media" element={<AdminMedia />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="settings" element={<AdminSettings />} />
                        <Route path="email-settings" element={<AdminEmailSettings />} />
                        <Route path="email-templates" element={<AdminEmailTemplates />} />
                        <Route path="email-bounces" element={<AdminEmailBounces />} />
                        <Route path="communications" element={<AdminCommunications />} />
                        <Route path="blog" element={<AdminBlog />} />
                        <Route path="seo" element={<AdminSEO />} />
                        <Route path="team" element={<AdminTeam />} />
                        <Route path="marketing" element={<AdminMarketing />} />
                        <Route path="stock" element={<AdminStock />} />
                        <Route path="tutorial" element={<AdminTutorial />} />
                        <Route path="pro" element={<AdminPro />} />
                        <Route path="leads" element={<AdminLeads />} />
                        <Route path="manual-orders" element={<AdminManualOrders />} />
                        <Route path="tasks" element={<AdminTasks />} />
                        <Route path="internal-links" element={<AdminInternalLinks />} />
                        <Route path="abonnements" element={<AdminSubscriptions />} />
                        <Route path="accounting" element={<AdminAccounting />} />
                      </Route>
                    </Routes>
                  </AdminGuard>
                </AdminProvider>
              } />
              
              {/* Checkout - special layout */}
              <Route 
              path="/checkout" 
              element={
                <CheckoutLayout>
                  <CheckoutPage />
                </CheckoutLayout>
              } 
            />
            
            {/* Checkout Success - special layout */}
            <Route 
              path="/checkout/success" 
              element={
                <CheckoutLayout>
                  <CheckoutSuccessPage />
                </CheckoutLayout>
              } 
            />

            {/* Resume Payment - special layout */}
            <Route 
              path="/checkout/pay/:orderId" 
              element={
                <CheckoutLayout>
                  <ResumePaymentPage />
                </CheckoutLayout>
              } 
            />
            
            {/* All other pages - main layout */}
            <Route 
              path="/*" 
              element={
                <MainLayout>
                  <Routes>
                    <Route index element={<HomePage />} />
                    <Route path="boutique" element={<ShopPage />} />
                    <Route path="categorie/:slug" element={<CategoryPage />} />
                    <Route path="produit/:slug" element={<ProductPage />} />
                    <Route path="abonnement" element={<AbonnementPage />} />
                    <Route path="abonnement/merci" element={<AbonnementMerciPage />} />
                    <Route path="routine" element={<RoutinePage />} />
                    <Route path="compte/*" element={<AccountPage />} />
                    <Route path="reset-password" element={<ResetPasswordPage />} />
                    <Route path="recherche" element={<SearchPage />} />
                    
                    {/* Static pages - placeholder */}
                    <Route path="a-propos" element={<AboutPage />} />
                    <Route path="drainage-lymphatique" element={<Navigate to="/blog/drainage-lymphatique" replace />} />
                    <Route path="contact" element={<ContactPage />} />
                    <Route path="formations-massages" element={<FormationsMassagesPage />} />
                    <Route path="carte-cadeau" element={<GiftCardPage />} />
                    <Route path="idees-cadeaux" element={<IdeesCadeauxPage />} />
                    <Route path="mentions-legales" element={<LegalPage />} />
                    <Route path="confidentialite" element={<LegalPage />} />
                    <Route path="politique-cookies" element={<LegalPage />} />
                    <Route path="cgv" element={<LegalPage />} />
                    <Route path="livraison" element={<LivraisonPage />} />
                    <Route path="retours" element={<RetoursPage />} />
                    <Route path="faq" element={<FAQPage />} />
                    <Route path="blog" element={<BlogPage />} />
                    <Route path="blog/:slug" element={<BlogPostPage />} />
                    <Route path="espace-pro/*" element={<ProPage />} />
                    {/* Influencer tracking shortlink: /ref/NomInfluenceur → home with ?ref=NomInfluenceur */}
                    <Route path="ref/:refCode" element={<InfluencerRedirect />} />
                    
                    {/* Public invoice via share token (UUID) */}
                    <Route path=":token" element={<PublicInvoiceRoute />} />

                    {/* 404 */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </MainLayout>
              } 
            />
          </Routes>
            </Suspense>
          <Suspense fallback={null}><Toaster position="top-center" /></Suspense>
          {nonCriticalReady ? (
            <Suspense fallback={null}>
              <CookieBanner />
              <TrackingManager />
            </Suspense>
          ) : null}
          </ProProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

// Influencer shortlink redirect: /ref/NomInfluenceur → /?ref=NomInfluenceur
function InfluencerRedirect() {
  const { refCode } = useParams();
  const target = `/?ref=${encodeURIComponent(refCode || '')}`;
  useEffect(() => {
    window.location.replace(target);
  }, [target]);
  return null;
}

// Placeholder for static pages
function StaticPage({ title }) {
  return (
    <div className="pb-nav">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-12">
        <h1 className="font-serif text-3xl text-brand-charcoal mb-6">{title}</h1>
        <div className="prose max-w-none text-brand-stone">
          <p>Contenu de la page {title} à venir.</p>
        </div>
      </div>
    </div>
  );
}

// Public invoice route — only activates for UUID-shaped tokens
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function PublicInvoiceRoute() {
  const { token } = useParams();
  if (!UUID_RE.test(token)) return <NotFoundPage />;
  return <PublicInvoicePage />;
}

// 404 Page
function NotFoundPage() {
  useEffect(() => {
    document.title = '404 — Page non trouvée | Body Look Care';
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'robots'; document.head.appendChild(meta); }
    meta.content = 'noindex, nofollow';
    return () => { meta.content = 'index, follow'; };
  }, []);
  return (
    <div className="pb-nav min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="font-serif text-6xl text-brand-forest mb-4">404</h1>
        <p className="text-brand-stone text-lg mb-6">Page non trouvée</p>
        <p className="text-brand-stone/70 text-sm mb-8">La page que vous cherchez n'existe pas ou a été déplacée.</p>
        <div className="flex gap-3 justify-center flex-wrap">
          <a href="/" className="btn-primary">Retour à l'accueil</a>
          <a href="/boutique" className="btn-secondary">Voir la boutique</a>
        </div>
      </div>
    </div>
  );
}

export default App;
